import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';



import { makeStyles } from 'tss-react/mui';
import {
  Box,
  Button,
  CustomPagination,
  MUIDataTable,
  MUIDataTableColumnDef,
  Progress,
  SearchBar,
  Typography,
} from 'web-library';
import EditButton from 'web-library/components/buttons/EditButton';
import DeleteButton from 'web-library/components/buttons/DeleteButton';
import {
  RequestTypeMode,
  UserGroupDetail,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { stringToLongDateAndShortTime } from 'web-library/utils/DateUtils';
import { usePostSearchUserGroup } from '../../../api-refactored/user-group-access-configuration/usePostSearchUserGroup';
import RouteKey from '../../../routes/key';
import DeleteUserGroupModal from './DeleteUserGroupModal';

const useStyles = makeStyles()((theme) =>
({
  datatableContainer: {
    marginBottom: theme.spacing(7),
  },
  searchContainer: {
    marginBottom: theme.spacing(3),
  },
  createdUpdatedByTime: {
    marginBottom: theme.spacing(1),
  },
  createdUpdatedByText: {
    color: theme.color.text.textTertiary,
  },
  actionContainer: {
    display: 'flex',
  },
  viewDetailsButton: {
    padding: '0',
    [theme.breakpoints.down('lg')]: {
      marginTop: theme.spacing(2),
    },
    marginRight: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  iconButtonContainer: {
    display: 'inline-flex',
    [theme.breakpoints.down(1245)]: {
      marginTop: theme.spacing(2),
      '& h5': { display: 'none' },
    },
  },
  editButtonContainer: {
    marginRight: theme.spacing(4),
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const {
  userGroupAccessConfiguration,
  editAccessConfiguration,
  viewAccessConfiguration,
  editUserGroup,
  viewUserGroup,
} = RouteKey.admin.child;
const { admin } = RouteKey.child;

const tableColumns = (
  data: UserGroupDetail[],
  requestType: RequestTypeMode,
  hooks: {
    classes: Record<string,string>;
    t: TFunction;
    history: RouteComponentProps['history'];
    setDeleteUserGroupName: React.Dispatch<React.SetStateAction<string>>;
    setOpenDeleteUserGroupModal: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteUserUuid: (uuid: string) => void;
  },
  hasEditPermission: boolean,
  hasDeletePermission: boolean
): MUIDataTableColumnDef[] => [
    {
      name: 'userGroupDisplayName',
      label: hooks.t('admin->>UserGroups.UserGroupName'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{data[dataIndex].userGroupDisplayName}</Typography>
        ),
      },
    },
    {
      name: 'groupSize',
      label: hooks.t('admin->>UserGroups.GroupSize'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{data[dataIndex].userAccounts?.length || 0}</Typography>
        ),
      },
    },
    {
      name: 'createDateTime',
      label: hooks.t('admin->>UserGroups.CreateDateTime'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography className={hooks.classes.createdUpdatedByTime} variant="body2">
              {stringToLongDateAndShortTime(data[dataIndex].createdAt)}
            </Typography>
            <Typography
              className={hooks.classes.createdUpdatedByText}
              variant="subtitle2"
            >{`by ${data[dataIndex].createdBy}`}</Typography>
          </>
        ),
      },
    },
    {
      name: 'lastUpdatedDateTime',
      label: hooks.t('admin->>UserGroups.LastUpdatedDateTime'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography className={hooks.classes.createdUpdatedByTime} variant="body2">
              {stringToLongDateAndShortTime(data[dataIndex].updatedAt)}
            </Typography>
            <Typography
              className={hooks.classes.createdUpdatedByText}
              variant="subtitle2"
            >{`by ${data[dataIndex].updatedBy}`}</Typography>
          </>
        ),
      },
    },
    {
      name: 'actions',
      label: hooks.t('admin->>UserGroups.Actions'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          const viewPath =
            requestType === RequestTypeMode.AdminAccessConfiguration
              ? `/${admin}/${userGroupAccessConfiguration.route}/${data[dataIndex].uuid}/${viewAccessConfiguration.route}`
              : `/${admin}/${userGroupAccessConfiguration.route}/${data[dataIndex].uuid}/${viewUserGroup.route}`;
          const editPath =
            requestType === RequestTypeMode.AdminAccessConfiguration
              ? `/${admin}/${userGroupAccessConfiguration.route}/${data[dataIndex].uuid}/${editAccessConfiguration.route}`
              : `/${admin}/${userGroupAccessConfiguration.route}/${data[dataIndex].uuid}/${editUserGroup.route}`;
          return (
            <Box className={hooks.classes.actionContainer}>
              <Button
                id="button-manage-notifications-view"
                className={hooks.classes.viewDetailsButton}
                onClick={() => viewPath && hooks.history.push(viewPath)}
                variant="text"
                color="primary"
                size="small"
              >
                <Typography variant="h5" align="left">
                  {hooks.t('announcementsNotifications->>Listing.ViewDetails')}
                </Typography>
              </Button>
              {hasEditPermission && (
                <Box
                  className={`${hooks.classes.iconButtonContainer}
                ${requestType === RequestTypeMode.AdminUserGroup
                      ? hooks.classes.editButtonContainer
                      : ''
                    }`}
                >
                  <EditButton
                    editHandler={() => editPath && hooks.history.push(editPath)}
                    label={hooks.t('announcementsNotifications->>Listing.Edit')}
                    typographyVariant="h5"
                  />
                </Box>
              )}
              {hasDeletePermission && requestType === RequestTypeMode.AdminUserGroup && (
                <Box className={hooks.classes.iconButtonContainer}>
                  <DeleteButton
                    deleteHandler={() => {
                      hooks.setDeleteUserGroupName(data[dataIndex].userGroupDisplayName);
                      hooks.setOpenDeleteUserGroupModal(true);
                      hooks.setDeleteUserUuid(data[dataIndex].uuid);
                    }}
                    label={hooks.t('announcementsNotifications->>Listing.Delete')}
                    typographyVariant="h5"
                    disabled={
                      data[dataIndex].userGroupName === 'ROLE_OPS_ADMIN_ASD_GROUP' ||
                      data[dataIndex].userGroupName === 'ROLE_OPS_ADMIN_MOE_GROUP' ||
                      data[dataIndex].userGroupName === 'ROLE_FINANCE_GROUP'
                    }
                  />
                </Box>
              )}
            </Box>
          );
        },
      },
    },
  ];

const TableFooter = ({
  classes,
  userGroupSearchData,
  pageNumber,
  rowsPerPage,
  setPageNumber,
  setRowsPerPage,
}: {
  classes: Record<string,string>;
  userGroupSearchData: UserGroupDetail[] | undefined;
  pageNumber: number;
  rowsPerPage: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
}) => (
  <Box className={classes.paginationContainer}>
    <CustomPagination
      dataLength={userGroupSearchData?.length || 0}
      page={pageNumber}
      rowsPerPage={rowsPerPage}
      rowsOptions={[5, 10, 20, 40, 50]}
      setPage={(currentPage: number) => setPageNumber(currentPage)}
      setRowsPerPage={(numberOfRows: number) => {
        setPageNumber(0);
        setRowsPerPage(numberOfRows);
      }}
    />
  </Box>
);

const AdminUserGroupDataTable = ({
  requestType,
  hasEditPermission,
  hasDeletePermission,
}: {
  requestType: RequestTypeMode;
  hasEditPermission: boolean;
  hasDeletePermission: boolean;
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [searchKeyword, setSearchKeyword] = useState('');
  const history = useHistory();

  const [deleteUserGroupName, setDeleteUserGroupName] = useState<string>('');
  const [openDeleteUserGroupModal, setOpenDeleteUserGroupModal] = useState<boolean>(false);
  const [deleteUserUuid, setDeleteUserUuid] = useState('');

  const [pageNumber, setPageNumber] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);

  const {
    data: userGroupSearchData,
    mutate: userGroupSearchMutate,
    isLoading: isUserGroupSearchLoading,
  } = usePostSearchUserGroup({});

  useEffect(() => {
    userGroupSearchMutate({ q: searchKeyword });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword]);

  if (isUserGroupSearchLoading) {
    return <Progress color="secondary" />;
  }

  return (
    <Box className={classes.datatableContainer}>
      <Box className={classes.searchContainer}>
        <SearchBar
          placeholder={t('admin->>UserGroups.SearchPlaceholder')}
          name="searchInput"
          ariaLabel="searchInput"
          defaultValue={searchKeyword}
          onSearch={(searchStr: string) => {
            setSearchKeyword(searchStr);
          }}
        />
      </Box>

      <MUIDataTable
        columns={tableColumns(
          userGroupSearchData || [],
          requestType,
          {
            classes,
            t,
            history,
            setDeleteUserGroupName,
            setOpenDeleteUserGroupModal,
            setDeleteUserUuid,
          },
          hasEditPermission,
          hasDeletePermission
        )}
        data={userGroupSearchData || []}
        key={`user-group-${requestType}`}
        options={{
          pagination: true,
          selectToolbarPlacement: 'none',
          serverSide: false,
          page: pageNumber,
          rowsPerPage,
          onChangePage: (currentPage: number) => setPageNumber(currentPage),
          onChangeRowsPerPage: (numberOfRows: number) => setRowsPerPage(numberOfRows),
        }}
        components={{
          TableFooter: () =>
            TableFooter({
              classes,
              userGroupSearchData,
              pageNumber,
              rowsPerPage,
              setPageNumber,
              setRowsPerPage,
            }),
        }}
        rowsPerPageOptions={[5, 10, 20, 40, 50]}
      />
      <DeleteUserGroupModal
        modalState={openDeleteUserGroupModal}
        setOpenModalState={setOpenDeleteUserGroupModal}
        deleteUserGroupName={deleteUserGroupName}
        deleteUserUuid={deleteUserUuid}
      />
    </Box>
  );
};

export default AdminUserGroupDataTable;
